import React, { useEffect, useState } from "react";

import { css } from "@emotion/css";
import { useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";

import { useStyles } from "@/hooks/useStyles.ts";
import { periodicities } from "@/pages/patient-monitoring/PatientMonitoringPeriod.ts";
import { usePatientMonitoringContext } from "@/pages/patient-monitoring/PatientMonitoringState.tsx";
import { SelectedPeriod } from "@/pages/patient-monitoring/SelectedPeriod.tsx";
import { Theme } from "@/theme/CreateTheme.ts";
import { PeriodPicker, SelectNextPeriod, SelectPreviousPeriod } from "@/uiKit/molecules/period-picker/PeriodPicker.tsx";
import { Periodicity } from "@/uiKit/molecules/period-picker/PeriodSelector.tsx";
import { DateUtils, toDateTime } from "@/utils/dateUtils.ts";

type HeaderMonitoringProps = {
  lockPeriod?: {
    periodicity: Periodicity;
  };
};

export const HeaderMonitoring: React.FC<HeaderMonitoringProps> = ({ lockPeriod }) => {
  const state = usePatientMonitoringContext(s => s);

  const [nextPeriodDisable, setNextPeriodDisable] = useState(true);

  const styles = useStyles(makeStyles);

  const navigate = useNavigate();

  useEffect(() => {
    const endDate = state.to;
    if (DateUtils.compareDates(endDate, DateTime.now().minus({ days: 1 })) >= 0) {
      setNextPeriodDisable(true);
    } else {
      setNextPeriodDisable(false);
    }
  }, [state.to]);

  const onChangePeriodicity = (newPeriod: Periodicity) => {
    const endDate = toDateTime(state.to);
    const newStart = endDate.minus({ days: newPeriod.durationInDays - 1 });

    const isoStart = newStart.toISODate();
    const isoEnd = endDate.toISODate();
    if (isoStart && isoEnd) {
      navigate({ to: ".", search: { from: isoStart, to: isoEnd } });
    }
  };

  const goToPreviousPeriodHandler = () => {
    const previousStart = toDateTime(state.from);
    const previousEnd = toDateTime(state.to);
    const periodicity = lockPeriod?.periodicity || state.periodicity;
    const newStart = previousStart.minus({
      days: periodicity.durationInDays,
    });

    const newEnd = previousEnd.minus({
      days: periodicity.durationInDays,
    });

    const isoStart = newStart.toISODate();
    const isoEnd = newEnd.toISODate();
    if (isoStart && isoEnd) {
      navigate({ to: ".", search: { from: isoStart, to: isoEnd } });
    }
  };

  const goToNextPeriodHandler = () => {
    const previousStart = toDateTime(state.from);
    const previousEnd = toDateTime(state.to);
    const periodicity = lockPeriod?.periodicity || state.periodicity;
    let newStart;
    let newEnd = previousEnd.plus({ days: periodicity.durationInDays });
    const today = DateTime.now();
    if (DateUtils.compareDates(newEnd, today) >= 0) {
      newEnd = today;
      newStart = today.minus({ days: periodicity.durationInDays });
    } else {
      newStart = previousStart.plus({ days: periodicity.durationInDays });
    }

    const isoStart = newStart.toISODate();
    const isoEnd = newEnd.toISODate();
    if (isoStart && isoEnd) {
      navigate({ to: ".", search: { from: isoStart, to: isoEnd } });
    }
  };

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.picker}>
          <div className={styles.period}>
            <SelectPreviousPeriod onClick={goToPreviousPeriodHandler} />
            <SelectedPeriod from={state.from} to={state.to} />
            <SelectNextPeriod onClick={goToNextPeriodHandler} disabled={nextPeriodDisable} />
          </div>
          {!lockPeriod && (
            <PeriodPicker
              key={state.periodicity.index}
              periodicities={periodicities}
              current={state.periodicity}
              onChange={onChangePeriodicity}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: ${theme.spacing(4)};
  `,
  picker: css`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    align-items: stretch;
    gap: ${theme.spacing(4)};
    flex-wrap: wrap;
  `,
  period: css`
    display: flex;
    flex-direction: row;
  `,
});
